import { motion } from 'framer-motion';
import { ShoppingBag, Shirt, HatGlasses, Briefcase, Coffee, Package } from 'lucide-react';
import HeroSection from '../components/HeroSection';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

const merchItems = [
  {
    title: 'Bizfund Signature Hat',
    subtitle: 'Structured cap placeholder',
    icon: HatGlasses,
    accent: 'from-[#0F172A] via-[#1D4ED8] to-[#38BDF8]',
  },
  {
    title: 'Bizfund Team Tee',
    subtitle: 'Premium shirt mockup',
    icon: Shirt,
    accent: 'from-[#111827] via-[#1E3A8A] to-[#60A5FA]',
  },
  {
    title: 'Broker Event Hoodie',
    subtitle: 'Outerwear showcase placeholder',
    icon: ShoppingBag,
    accent: 'from-[#172554] via-[#1D4ED8] to-[#93C5FD]',
  },
  {
    title: 'Office Tote Bag',
    subtitle: 'Conference giveaway concept',
    icon: Briefcase,
    accent: 'from-[#0F172A] via-[#334155] to-[#94A3B8]',
  },
  {
    title: 'Branded Coffee Mug',
    subtitle: 'Desk merch preview',
    icon: Coffee,
    accent: 'from-[#1E293B] via-[#2563EB] to-[#7DD3FC]',
  },
  {
    title: 'Packaging Kit',
    subtitle: 'Box and insert placeholder',
    icon: Package,
    accent: 'from-[#0B1120] via-[#1E40AF] to-[#38BDF8]',
  },
];

export default function Merchandise() {
  return (
    <main className="bg-[#F8FAFC] text-gray-900">
      <HeroSection
        tag="Bizfund Merchandise"
        title="Bizfund merchandise placeholders for upcoming branded items"
        subtitle="This page is ready for your future merch photos. For now, it presents polished placeholders for the kinds of branded items companies typically showcase."
        image="/images/offline/photo-1454165804606-c3d57bc86b40.jpg"
        sectionClassName="min-h-[500px] h-[72svh] md:min-h-[560px] md:h-[76vh]"
      />

      <section className="page-section bg-[#F8FAFC]">
        <div className="page-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="section-intro-wide mx-auto max-w-3xl text-center"
          >
            <span className="section-label">Placeholder Gallery</span>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 md:text-5xl">
              Future merch can drop right in here.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-gray-600 md:text-lg">
              Each card is built as a premium placeholder for real Bizfund product photography.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid gap-5 md:grid-cols-2 xl:grid-cols-3"
          >
            {merchItems.map((item) => {
              const Icon = item.icon;

              return (
                <motion.article
                  key={item.title}
                  variants={fadeUp}
                  className="overflow-hidden rounded-[30px] border border-gray-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)]"
                >
                  <div
                    className={`relative flex aspect-[4/3] items-center justify-center bg-gradient-to-br ${item.accent}`}
                  >
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.24),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(251,191,36,0.16),transparent_24%)]" />
                    <div className="relative flex h-28 w-28 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white shadow-[0_16px_40px_rgba(15,23,42,0.18)] backdrop-blur-sm">
                      <Icon size={46} />
                    </div>
                    <div className="absolute bottom-5 left-5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur-sm">
                      Image Placeholder
                    </div>
                  </div>

                  <div className="space-y-2 p-5 md:p-6">
                    <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-gray-600">{item.subtitle}</p>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>
    </main>
  );
}
